import logger from "../utils/logger";
import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../components/ui/dialog';
import { 
    MapPin, 
    Navigation, 
    Clock, 
    CheckCircle, 
    AlertCircle, 
    Plus,
    Building2,
    Users,
    Loader2,
    RefreshCw
} from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom icons
const officeIcon = new L.Icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
});

const employeeIcon = new L.Icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
});

const fieldStaffIcon = new L.Icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-orange.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
});

const currentLocationIcon = new L.Icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
});

import API_URL from '../utils/api';

// Component to recenter map
function RecenterMap({ position }) {
    const map = useMap();
    useEffect(() => {
        if (position) {
            map.setView(position, 15);
        }
    }, [position, map]);
    return null;
}

const GeoTracking = () => {
    const { user, canManage } = useAuth();
    const [currentLocation, setCurrentLocation] = useState(null);
    const [locationError, setLocationError] = useState(null);
    const [isLoadingLocation, setIsLoadingLocation] = useState(false);
    const [todayAttendance, setTodayAttendance] = useState(null);
    const [officeLocations, setOfficeLocations] = useState([]);
    const [liveLocations, setLiveLocations] = useState([]);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [officeFormData, setOfficeFormData] = useState({
        name: '',
        address: '',
        latitude: '',
        longitude: '',
        radius_meters: '100'
    });
    const [customerInfo, setCustomerInfo] = useState({
        customer_name: '',
        customer_address: '',
        notes: ''
    });
    const [isCheckingIn, setIsCheckingIn] = useState(false);
    const [isCheckingOut, setIsCheckingOut] = useState(false);
    const locationWatchId = useRef(null);
    const trackingIntervalId = useRef(null);

    const fetchData = useCallback(async () => {
        try {
            const [attendanceRes, officesRes] = await Promise.all([
                axios.get(`${API_URL}/api/attendance/today`, { withCredentials: true }),
                axios.get(`${API_URL}/api/office-locations`, { withCredentials: true })
            ]);
            setTodayAttendance(attendanceRes.data);
            setOfficeLocations(officesRes.data);

            if (canManage) {
                const { data: live } = await axios.get(`${API_URL}/api/attendance/live-locations`, { withCredentials: true });
                setLiveLocations(live);
            }
        } catch (error) {
            logger.error('Error fetching data:', error);
        }
    }, [canManage]);

    const getCurrentLocation = useCallback(() => {
        setIsLoadingLocation(true);
        setLocationError(null);

        if (!navigator.geolocation) {
            setLocationError('Geolocation is not supported by your browser');
            setIsLoadingLocation(false);
            return;
        }

        navigator.geolocation.getCurrentPosition(
            (position) => {
                setCurrentLocation({
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude,
                    accuracy: position.coords.accuracy
                });
                setIsLoadingLocation(false);
            },
            (error) => {
                setLocationError(`Unable to get location: ${error.message}`);
                setIsLoadingLocation(false);
            },
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
        );
    }, []);

    const startLiveTracking = useCallback(() => {
        // Update location every 5 minutes
        trackingIntervalId.current = setInterval(async () => {
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(
                    async (position) => {
                        try {
                            await axios.post(`${API_URL}/api/attendance/update-location`, {
                                latitude: position.coords.latitude,
                                longitude: position.coords.longitude,
                                accuracy: position.coords.accuracy,
                                battery_level: navigator.getBattery ? (await navigator.getBattery()).level * 100 : null
                            }, { withCredentials: true });
                            
                            setCurrentLocation({
                                latitude: position.coords.latitude,
                                longitude: position.coords.longitude,
                                accuracy: position.coords.accuracy
                            });
                        } catch (error) {
                            logger.error('Error updating location:', error);
                        }
                    },
                    (error) => logger.error('Location error:', error),
                    { enableHighAccuracy: true }
                );
            }
        }, 300000); // 5 minutes
    }, []);

    useEffect(() => {
        fetchData();
        getCurrentLocation();
        
        return () => {
            if (locationWatchId.current) {
                navigator.geolocation.clearWatch(locationWatchId.current);
            }
            if (trackingIntervalId.current) {
                clearInterval(trackingIntervalId.current);
            }
        };
    }, [fetchData, getCurrentLocation]);

    // Start live tracking when checked in
    useEffect(() => {
        if (todayAttendance?.check_in && !todayAttendance?.check_out) {
            startLiveTracking();
        }
        return () => {
            if (trackingIntervalId.current) {
                clearInterval(trackingIntervalId.current);
            }
        };
    }, [todayAttendance, startLiveTracking]);

    const handleCheckIn = async () => {
        if (!currentLocation) {
            getCurrentLocation();
            return;
        }

        setIsCheckingIn(true);
        try {
            const payload = {
                latitude: currentLocation.latitude,
                longitude: currentLocation.longitude,
                accuracy: currentLocation.accuracy,
                ...customerInfo
            };

            await axios.post(`${API_URL}/api/attendance/check-in`, payload, { withCredentials: true });
            fetchData();
            setCustomerInfo({ customer_name: '', customer_address: '', notes: '' });
        } catch (error) {
            alert(error.response?.data?.detail || 'Check-in failed');
        } finally {
            setIsCheckingIn(false);
        }
    };

    const handleCheckOut = async () => {
        if (!currentLocation) {
            getCurrentLocation();
            return;
        }

        setIsCheckingOut(true);
        try {
            await axios.post(`${API_URL}/api/attendance/check-out`, {
                latitude: currentLocation.latitude,
                longitude: currentLocation.longitude,
                accuracy: currentLocation.accuracy
            }, { withCredentials: true });
            fetchData();
        } catch (error) {
            alert(error.response?.data?.detail || 'Check-out failed');
        } finally {
            setIsCheckingOut(false);
        }
    };

    const handleAddOffice = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${API_URL}/api/office-locations`, {
                ...officeFormData,
                latitude: parseFloat(officeFormData.latitude),
                longitude: parseFloat(officeFormData.longitude),
                radius_meters: parseFloat(officeFormData.radius_meters)
            }, { withCredentials: true });
            setIsDialogOpen(false);
            setOfficeFormData({ name: '', address: '', latitude: '', longitude: '', radius_meters: '100' });
            fetchData();
        } catch (error) {
            alert(error.response?.data?.detail || 'Error adding office location');
        }
    };

    const useCurrentLocationForOffice = () => {
        if (currentLocation) {
            setOfficeFormData({
                ...officeFormData,
                latitude: currentLocation.latitude.toString(),
                longitude: currentLocation.longitude.toString()
            });
        }
    };

    const mapCenter = currentLocation 
        ? [currentLocation.latitude, currentLocation.longitude]
        : officeLocations.length > 0 
            ? [officeLocations[0].latitude, officeLocations[0].longitude]
            : [28.6139, 77.2090]; // Default to Delhi

    const isFieldStaff = user?.employee_type === 'field';

    return (
        <div className="space-y-6" data-testid="geo-tracking-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-black text-slate-900 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
                        Geo Location Tracking
                    </h1>
                    <p className="text-slate-500 mt-1">
                        {canManage ? 'Track employee locations in real-time' : 'Check-in with your location'}
                    </p>
                </div>

                {canManage && (
                    <div className="flex gap-3">
                        <Button 
                            variant="outline" 
                            className="rounded-none" 
                            onClick={fetchData}
                            data-testid="refresh-locations-btn"
                        >
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Refresh
                        </Button>
                        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                            <DialogTrigger asChild>
                                <Button className="bg-brand hover:bg-brand rounded-none" data-testid="add-office-btn">
                                    <Plus className="h-4 w-4 mr-2" />
                                    Add Office Location
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="rounded-none">
                                <DialogHeader>
                                    <DialogTitle className="text-xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
                                        Add Office Location
                                    </DialogTitle>
                                </DialogHeader>
                                <form onSubmit={handleAddOffice} className="space-y-4 mt-4">
                                    <div>
                                        <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Office Name</Label>
                                        <Input
                                            value={officeFormData.name}
                                            onChange={(e) => setOfficeFormData({...officeFormData, name: e.target.value})}
                                            className="rounded-none mt-1"
                                            placeholder="Head Office"
                                            required
                                            data-testid="office-name-input"
                                        />
                                    </div>
                                    <div>
                                        <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Address</Label>
                                        <Input
                                            value={officeFormData.address}
                                            onChange={(e) => setOfficeFormData({...officeFormData, address: e.target.value})}
                                            className="rounded-none mt-1"
                                            placeholder="123 Business Street, City"
                                            required
                                            data-testid="office-address-input"
                                        />
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Latitude</Label>
                                            <Input
                                                type="number"
                                                step="any"
                                                value={officeFormData.latitude}
                                                onChange={(e) => setOfficeFormData({...officeFormData, latitude: e.target.value})}
                                                className="rounded-none mt-1"
                                                required
                                                data-testid="office-latitude-input"
                                            />
                                        </div>
                                        <div>
                                            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Longitude</Label>
                                            <Input
                                                type="number"
                                                step="any"
                                                value={officeFormData.longitude}
                                                onChange={(e) => setOfficeFormData({...officeFormData, longitude: e.target.value})}
                                                className="rounded-none mt-1"
                                                required
                                                data-testid="office-longitude-input"
                                            />
                                        </div>
                                    </div>
                                    <Button 
                                        type="button" 
                                        variant="outline" 
                                        className="rounded-none w-full"
                                        onClick={useCurrentLocationForOffice}
                                        disabled={!currentLocation}
                                    >
                                        <Navigation className="h-4 w-4 mr-2" />
                                        Use My Current Location
                                    </Button>
                                    <div>
                                        <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Geofence Radius (meters)</Label>
                                        <Input
                                            type="number"
                                            value={officeFormData.radius_meters}
                                            onChange={(e) => setOfficeFormData({...officeFormData, radius_meters: e.target.value})}
                                            className="rounded-none mt-1"
                                            required
                                            data-testid="office-radius-input"
                                        />
                                    </div>
                                    <div className="flex justify-end gap-3 pt-4">
                                        <Button type="button" variant="outline" className="rounded-none" onClick={() => setIsDialogOpen(false)}>
                                            Cancel
                                        </Button>
                                        <Button type="submit" className="bg-brand hover:bg-brand rounded-none" data-testid="office-save-btn">
                                            Add Office
                                        </Button>
                                    </div>
                                </form>
                            </DialogContent>
                        </Dialog>
                    </div>
                )}
            </div>

            {/* Current Location & Check-in Card */}
            <Card className="rounded-none border-slate-200" data-testid="checkin-card">
                <CardHeader className="border-b border-slate-200">
                    <CardTitle className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                        <MapPin className="h-5 w-5 text-brand" />
                        Your Location & Attendance
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Location Status */}
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <span className="text-sm font-medium text-slate-600">Current Location</span>
                                <Button 
                                    size="sm" 
                                    variant="outline" 
                                    className="rounded-none"
                                    onClick={getCurrentLocation}
                                    disabled={isLoadingLocation}
                                >
                                    {isLoadingLocation ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                        <Navigation className="h-4 w-4" />
                                    )}
                                </Button>
                            </div>

                            {locationError && (
                                <div className="bg-red-50 border border-red-200 p-3 flex items-start gap-2">
                                    <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
                                    <p className="text-sm text-red-800">{locationError}</p>
                                </div>
                            )}

                            {currentLocation && (
                                <div className="bg-green-50 border border-green-200 p-3">
                                    <div className="flex items-center gap-2 text-green-800">
                                        <CheckCircle className="h-5 w-5" />
                                        <span className="font-medium">Location acquired</span>
                                    </div>
                                    <p className="text-sm text-green-700 mt-1">
                                        Lat: {currentLocation.latitude.toFixed(6)}, Lng: {currentLocation.longitude.toFixed(6)}
                                    </p>
                                    {currentLocation.accuracy && (
                                        <p className="text-xs text-green-600 mt-1">
                                            Accuracy: ±{Math.round(currentLocation.accuracy)}m
                                        </p>
                                    )}
                                </div>
                            )}

                            {/* Attendance Status */}
                            <div className="pt-4 border-t border-slate-200">
                                {todayAttendance?.check_in ? (
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-2">
                                            <CheckCircle className="h-5 w-5 text-green-600" />
                                            <span className="text-slate-700">
                                                Checked in at {new Date(todayAttendance.check_in).toLocaleTimeString()}
                                            </span>
                                        </div>
                                        {todayAttendance.check_in_location && (
                                            <p className="text-sm text-slate-500 pl-7">
                                                Location: {todayAttendance.check_in_location.latitude.toFixed(4)}, {todayAttendance.check_in_location.longitude.toFixed(4)}
                                            </p>
                                        )}
                                        {todayAttendance.nearest_office && (
                                            <p className="text-sm text-slate-500 pl-7">
                                                Office: {todayAttendance.nearest_office} ({todayAttendance.distance_from_office}m away)
                                            </p>
                                        )}
                                        {todayAttendance.check_out && (
                                            <div className="flex items-center gap-2">
                                                <CheckCircle className="h-5 w-5 text-green-600" />
                                                <span className="text-slate-700">
                                                    Checked out at {new Date(todayAttendance.check_out).toLocaleTimeString()}
                                                </span>
                                            </div>
                                        )}
                                        {todayAttendance.hours_worked && (
                                            <p className="text-sm font-medium text-slate-700 pl-7">
                                                Hours worked: {todayAttendance.hours_worked} hrs
                                            </p>
                                        )}
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-2">
                                        <Clock className="h-5 w-5 text-amber-500" />
                                        <span className="text-slate-600">Not checked in yet</span>
                                    </div>
                                )}
                            </div>

                            {/* Field Staff Customer Info */}
                            {isFieldStaff && !todayAttendance?.check_in && (
                                <div className="pt-4 space-y-3">
                                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Customer Details (Optional)</Label>
                                    <Input
                                        placeholder="Customer Name"
                                        value={customerInfo.customer_name}
                                        onChange={(e) => setCustomerInfo({...customerInfo, customer_name: e.target.value})}
                                        className="rounded-none"
                                        data-testid="customer-name-input"
                                    />
                                    <Input
                                        placeholder="Customer Address"
                                        value={customerInfo.customer_address}
                                        onChange={(e) => setCustomerInfo({...customerInfo, customer_address: e.target.value})}
                                        className="rounded-none"
                                        data-testid="customer-address-input"
                                    />
                                    <Textarea
                                        placeholder="Notes"
                                        value={customerInfo.notes}
                                        onChange={(e) => setCustomerInfo({...customerInfo, notes: e.target.value})}
                                        className="rounded-none"
                                        rows={2}
                                        data-testid="customer-notes-input"
                                    />
                                </div>
                            )}
                        </div>

                        {/* Check-in/out Buttons */}
                        <div className="flex flex-col justify-center gap-4">
                            <Button
                                onClick={handleCheckIn}
                                disabled={!currentLocation || todayAttendance?.check_in || isCheckingIn}
                                className="h-16 text-lg bg-green-600 hover:bg-green-700 rounded-none"
                                data-testid="geo-check-in-btn"
                            >
                                {isCheckingIn ? (
                                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                                ) : (
                                    <MapPin className="h-5 w-5 mr-2" />
                                )}
                                Check In with Location
                            </Button>
                            <Button
                                onClick={handleCheckOut}
                                disabled={!currentLocation || !todayAttendance?.check_in || todayAttendance?.check_out || isCheckingOut}
                                variant="outline"
                                className="h-16 text-lg rounded-none border-slate-300"
                                data-testid="geo-check-out-btn"
                            >
                                {isCheckingOut ? (
                                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                                ) : (
                                    <Clock className="h-5 w-5 mr-2" />
                                )}
                                Check Out with Location
                            </Button>

                            <div className="flex items-center gap-2 justify-center">
                                <Badge className={`rounded-none ${isFieldStaff ? 'bg-orange-100 text-orange-800 border-orange-200' : 'bg-blue-100 text-blue-800 border-blue-200'}`}>
                                    {isFieldStaff ? 'Field Staff' : 'Office Employee'}
                                </Badge>
                                {!isFieldStaff && (
                                    <span className="text-xs text-slate-500">
                                        (Geofence: 100m)
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Map Section */}
            <Card className="rounded-none border-slate-200" data-testid="map-card">
                <CardHeader className="border-b border-slate-200">
                    <CardTitle className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                        <Navigation className="h-5 w-5 text-brand" />
                        {canManage ? 'Live Employee Locations' : 'Location Map'}
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                    <div className="h-[500px] w-full">
                        <MapContainer
                            center={mapCenter}
                            zoom={13}
                            style={{ height: '100%', width: '100%' }}
                            className="z-0"
                        >
                            <TileLayer
                                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            />
                            
                            {currentLocation && <RecenterMap position={[currentLocation.latitude, currentLocation.longitude]} />}

                            {/* Current Location Marker */}
                            {currentLocation && (
                                <Marker 
                                    position={[currentLocation.latitude, currentLocation.longitude]}
                                    icon={currentLocationIcon}
                                >
                                    <Popup>
                                        <strong>Your Location</strong><br />
                                        Lat: {currentLocation.latitude.toFixed(6)}<br />
                                        Lng: {currentLocation.longitude.toFixed(6)}
                                    </Popup>
                                </Marker>
                            )}

                            {/* Office Locations with Geofence Circles */}
                            {officeLocations.filter(o => o.is_active).map((office) => (
                                <div key={office._id}>
                                    <Marker 
                                        position={[office.latitude, office.longitude]}
                                        icon={officeIcon}
                                    >
                                        <Popup>
                                            <strong>{office.name}</strong><br />
                                            {office.address}<br />
                                            <span className="text-xs text-gray-500">Radius: {office.radius_meters}m</span>
                                        </Popup>
                                    </Marker>
                                    <Circle
                                        center={[office.latitude, office.longitude]}
                                        radius={office.radius_meters}
                                        pathOptions={{
                                            color: '#2563eb',
                                            fillColor: '#3b82f6',
                                            fillOpacity: 0.1
                                        }}
                                    />
                                </div>
                            ))}

                            {/* Live Employee Locations (Admin/HR only) */}
                            {canManage && liveLocations.map((emp) => (
                                <Marker 
                                    key={emp.employee_id}
                                    position={[emp.latitude, emp.longitude]}
                                    icon={emp.employee_type === 'field' ? fieldStaffIcon : employeeIcon}
                                >
                                    <Popup>
                                        <strong>{emp.employee_name}</strong><br />
                                        <Badge className={`text-xs ${emp.employee_type === 'field' ? 'bg-orange-100 text-orange-800' : 'bg-green-100 text-green-800'}`}>
                                            {emp.employee_type === 'field' ? 'Field Staff' : 'Office'}
                                        </Badge><br />
                                        {emp.customer_name && <span>Customer: {emp.customer_name}<br /></span>}
                                        <span className="text-xs text-gray-500">
                                            Checked in: {new Date(emp.check_in_time).toLocaleTimeString()}
                                        </span>
                                    </Popup>
                                </Marker>
                            ))}
                        </MapContainer>
                    </div>
                </CardContent>
            </Card>

            {/* Office Locations List (Admin only) */}
            {canManage && (
                <Card className="rounded-none border-slate-200" data-testid="office-locations-card">
                    <CardHeader className="border-b border-slate-200">
                        <CardTitle className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                            <Building2 className="h-5 w-5 text-brand" />
                            Office Locations ({officeLocations.length})
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                        <div className="overflow-x-auto">
                            <table className="w-full" data-testid="office-locations-table">
                                <thead>
                                    <tr className="border-b border-slate-200 bg-slate-50">
                                        <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Office Name</th>
                                        <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Address</th>
                                        <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Coordinates</th>
                                        <th className="text-center p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Radius</th>
                                        <th className="text-center p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {officeLocations.map((office) => (
                                        <tr key={office._id} className="border-b border-slate-200 hover:bg-slate-50">
                                            <td className="p-4 font-medium text-slate-900">{office.name}</td>
                                            <td className="p-4 text-slate-700">{office.address}</td>
                                            <td className="p-4 text-slate-600 text-sm">
                                                {office.latitude.toFixed(6)}, {office.longitude.toFixed(6)}
                                            </td>
                                            <td className="p-4 text-center text-slate-700">{office.radius_meters}m</td>
                                            <td className="p-4 text-center">
                                                <Badge className={`rounded-none ${office.is_active ? 'bg-green-100 text-green-800 border-green-200' : 'bg-slate-100 text-slate-800 border-slate-200'}`}>
                                                    {office.is_active ? 'Active' : 'Inactive'}
                                                </Badge>
                                            </td>
                                        </tr>
                                    ))}
                                    {officeLocations.length === 0 && (
                                        <tr>
                                            <td colSpan="5" className="p-8 text-center text-slate-500">
                                                No office locations configured. Add one to enable geofencing.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Live Tracking Summary (Admin/HR only) */}
            {canManage && liveLocations.length > 0 && (
                <Card className="rounded-none border-slate-200" data-testid="live-tracking-card">
                    <CardHeader className="border-b border-slate-200">
                        <CardTitle className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                            <Users className="h-5 w-5 text-green-600" />
                            Currently Active ({liveLocations.length})
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                        <div className="divide-y divide-slate-200">
                            {liveLocations.map((emp) => (
                                <div key={emp.employee_id} className="p-4 flex items-center justify-between hover:bg-slate-50">
                                    <div className="flex items-center gap-3">
                                        <div className={`p-2 rounded-full ${emp.employee_type === 'field' ? 'bg-orange-100' : 'bg-green-100'}`}>
                                            <MapPin className={`h-4 w-4 ${emp.employee_type === 'field' ? 'text-orange-600' : 'text-green-600'}`} />
                                        </div>
                                        <div>
                                            <p className="font-medium text-slate-900">{emp.employee_name}</p>
                                            <p className="text-sm text-slate-500">
                                                {emp.employee_type === 'field' ? 'Field Staff' : 'Office'} 
                                                {emp.customer_name && ` • At: ${emp.customer_name}`}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-sm text-slate-600">
                                            {emp.latitude.toFixed(4)}, {emp.longitude.toFixed(4)}
                                        </p>
                                        <p className="text-xs text-slate-500">
                                            Since {new Date(emp.check_in_time).toLocaleTimeString()}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
};

export default GeoTracking;
